import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Checkbox visual variants — mirror the Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, node `3418:2713` ("Checkbox"). Reference cells:
 *   - 3418:2703 – Large / Default            (unselected, secondary-300 border)
 *   - 3418:2707 – Large / Hover              (border deepens to secondary-400)
 *   - 3418:2697 – Large / Selected / Default (secondary-50 fill, secondary-600 check)
 *   - 3479:3247 – Large / Selected / Primary (primary-500 fill, white check)
 *   - 3418:2698 – Large / Focus-Default      (secondary-600 focus ring)
 *   - 3479:3249 – Large / Focus-Selected / Primary (primary-900 focus ring)
 *
 * Anatomy: a single bordered `Pressable` box that owns size + radius + the
 * (web-only) keyboard focus ring, with a centered `Check` icon shown only when
 * `checked`. No custom animation — the Figma component has no motion spec.
 *
 * Axes:
 *   size:    s (16px) · m (20px, default) · l (24px). Radius scales 5/6/8px.
 *   variant: `default` (neutral secondary fill + gray check) · `primary`
 *            (brand purple fill + white check). Figma's "Type" property.
 *   selected: the checked axis (surfaced as `checked`).
 *
 * Colors (light, from `get_variable_defs` on node 3418:2713):
 *   glass fill     brand/secondary/30 = rgba(255,255,255,.7) → `secondary-50/70`
 *                  (auto-inverts to a translucent dark) over a 4px backdrop blur
 *                  (Figma "Glass" effect, radius 4). Blur is web-only; native
 *                  keeps the translucent fill — no backdrop-filter (awaits a BlurView).
 *   unselected     glass fill · border secondary-400 · hover border secondary-500
 *   selected default  glass fill · border secondary-500 · check secondary-600
 *   selected primary  bg primary-500 (solid, no glass) · border primary-600 · check white
 *   focus ring     default secondary-600 · primary primary-900
 *
 * Disabled: `opacity-50` (Figma disabled cells = state + opacity) — supplied by
 * the `Pressable` primitive on both platforms, so no CVA override is needed.
 *
 * Dark mode is automatic: every token above resolves through the auto-inverting
 * CSS-var palette (`generate-css-vars.cjs` mirrors each shade `dark = 1000 −
 * light`, `500` fixed), so NO `dark:` classes are needed. The lone exception is
 * the check *icon* color, which lucide reads as a hex `color` prop rather than a
 * class — see `Checkbox.icons.ts` for its per-mode resolution.
 *
 * Glass update (node 3418:2713): the Default variant gained the DS "Glass"
 * material — a `secondary-50/70` translucent fill + 4px web backdrop blur — on
 * BOTH its unselected and selected states. This supersedes the earlier
 * transparent / `secondary-50` fills and folds in two prior border drifts:
 * every Default state now shares one border ramp at all sizes — secondary-400
 * (unselected / disabled) → secondary-500 (hover / selected). Primary is
 * unchanged: a solid primary-500 fill, no glass.
 */
export const checkboxVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center border web:align-middle ' +
    'web:focus-visible:ring-1 web:focus-visible:ring-offset-0 ' +
    'web:transition-colors web:duration-150',
  {
    variants: {
      size: {
        s: 'h-4 w-4 rounded-[5px]',
        m: 'h-5 w-5 rounded-[6px]',
        l: 'h-6 w-6 rounded-[8px]',
      },
      // Focus-ring color is keyed off the type, matching the Figma focus cells.
      variant: {
        default: 'web:focus-visible:ring-secondary-600',
        primary: 'web:focus-visible:ring-primary-900',
      },
      selected: { true: '', false: '' },
    },
    compoundVariants: [
      // Unselected — glass fill; border deepens on hover (web pointer only).
      {
        selected: false,
        class:
          'bg-secondary-50/70 web:backdrop-blur-[1px] border-secondary-400 ' +
          'web:hover:border-secondary-500',
      },
      // Selected / Default type — glass fill + gray check.
      {
        selected: true,
        variant: 'default',
        class: 'bg-secondary-50/70 web:backdrop-blur-[2px] border-secondary-500',
      },
      // Selected / Primary type — solid brand fill + white check (no glass).
      { selected: true, variant: 'primary', class: 'bg-primary-500 border-primary-600' },
    ],
    defaultVariants: { size: 'm', variant: 'default', selected: false },
  },
);

export type CheckboxVariantProps = VariantProps<typeof checkboxVariants>;
